/**
 * Theme Store (Pinia)
 * Manages global theme state for dynamic color changes
 * Syncs navbar, elements, and background colors
 */

import { defineStore } from 'pinia';
import { useAuthStore } from './auth';

export const useThemeStore = defineStore('theme', {
  state: () => ({
    currentTheme: 'default', // 'default' (green), 'medical' (blue), 'admin' (dark)
    manualOverride: null // Allows manual theme override
  }),

  getters: {
    /**
     * Get the active theme based on user role or manual override
     */
    activeTheme: (state) => {
      // Manual override takes priority
      if (state.manualOverride) {
        return state.manualOverride;
      }
      
      // Auto-detect from auth store
      const authStore = useAuthStore();
      const role = authStore.role;
      
      if (role === 'Admin') {
        return 'admin';
      }
      if (role === 'Veto') {
        return 'medical';
      }
      
      return 'default';
    },

    /**
     * Get theme class name for CSS
     */
    themeClass: (state) => {
      const theme = state.manualOverride || state.currentTheme;
      return `theme-${theme}`;
    },

    /**
     * Check if using dark theme (admin)
     */
    isDarkTheme: (state) => {
      const authStore = useAuthStore();
      return state.manualOverride === 'admin' || authStore.role === 'Admin';
    },

    /**
     * Get primary color based on theme
     */
    primaryColor: (state) => {
      const authStore = useAuthStore();
      const theme = state.manualOverride || (authStore.role === 'Veto' ? 'medical' : 'default');
      
      const colors = {
        default: '#59a96a',
        medical: '#3b88a5',
        admin: '#3b88a5'
      };
      return colors[theme] || colors.default;
    },

    /**
     * Get accent color based on theme
     */
    accentColor: (state) => {
      const authStore = useAuthStore();
      const theme = state.manualOverride || (authStore.role === 'Veto' ? 'medical' : 'default');
      
      const colors = {
        default: '#3d8b4f',
        medical: '#2d6b84',
        admin: '#59a96a'
      };
      return colors[theme] || colors.default;
    }
  },

  actions: {
    /**
     * Set theme based on user role (called on login/role change)
     */
    setThemeFromRole(role) {
      if (role === 'Admin') {
        this.currentTheme = 'admin';
      } else if (role === 'Veto') {
        this.currentTheme = 'medical';
      } else {
        this.currentTheme = 'default';
      }
      this.applyTheme();
    },

    /**
     * Manually set theme (overrides auto-detection)
     */
    setTheme(theme) {
      if (['default', 'medical', 'admin'].includes(theme)) {
        this.manualOverride = theme;
        this.currentTheme = theme;
        this.applyTheme();
      }
    },

    /**
     * Clear manual override, return to auto-detection
     */
    clearOverride() {
      this.manualOverride = null;
      const authStore = useAuthStore();
      this.setThemeFromRole(authStore.role);
    },

    /**
     * Apply theme to document root for global CSS variable access
     */
    applyTheme() {
      const html = document.documentElement;
      
      // Remove existing theme classes
      html.classList.remove('theme-default', 'theme-medical', 'theme-admin');
      
      // Add current theme class
      const themeClass = `theme-${this.currentTheme}`;
      html.classList.add(themeClass);
      
      // Update CSS custom properties dynamically
      this.updateCSSVariables();
    },

    /**
     * Update CSS custom properties based on current theme
     */
    updateCSSVariables() {
      const root = document.documentElement;
      
      const themes = {
        default: {
          '--theme-primary': '#59a96a',
          '--theme-primary-light': '#7bc08a',
          '--theme-primary-dark': '#3d8b4f',
          '--theme-primary-rgb': '89, 169, 106',
          '--theme-bg-start': 'rgba(240, 255, 240, 1)',
          '--theme-bg-mid': 'rgba(225, 248, 230, 1)',
          '--theme-bg-end': 'rgba(240, 255, 240, 1)',
          '--theme-scrolled-bg': 'rgba(240, 255, 240, 0.92)',
          '--theme-hover-bg': 'rgba(89, 169, 106, 0.15)',
          '--theme-active-bg': 'rgba(89, 169, 106, 0.2)',
          '--theme-border-color': 'rgba(89, 169, 106, 0.2)'
        },
        medical: {
          '--theme-primary': '#3b88a5',
          '--theme-primary-light': '#5aa3c0',
          '--theme-primary-dark': '#2d6b84',
          '--theme-primary-rgb': '59, 136, 165',
          '--theme-bg-start': 'rgba(240, 248, 255, 1)',
          '--theme-bg-mid': 'rgba(225, 242, 252, 1)',
          '--theme-bg-end': 'rgba(240, 248, 255, 1)',
          '--theme-scrolled-bg': 'rgba(240, 248, 255, 0.92)',
          '--theme-hover-bg': 'rgba(59, 136, 165, 0.15)',
          '--theme-active-bg': 'rgba(59, 136, 165, 0.2)',
          '--theme-border-color': 'rgba(59, 136, 165, 0.2)'
        },
        admin: {
          '--theme-primary': '#3b88a5',
          '--theme-primary-light': '#5aa3c0',
          '--theme-primary-dark': '#2d6b84',
          '--theme-primary-rgb': '59, 136, 165',
          '--theme-bg-start': 'rgba(26, 31, 54, 1)',
          '--theme-bg-mid': 'rgba(18, 22, 40, 1)',
          '--theme-bg-end': 'rgba(26, 31, 54, 1)',
          '--theme-scrolled-bg': 'rgba(26, 31, 54, 0.95)',
          '--theme-hover-bg': 'rgba(59, 136, 165, 0.2)',
          '--theme-active-bg': 'rgba(59, 136, 165, 0.3)',
          '--theme-border-color': 'rgba(59, 136, 165, 0.3)'
        }
      };
      
      const themeVars = themes[this.currentTheme] || themes.default;
      
      Object.entries(themeVars).forEach(([property, value]) => {
        root.style.setProperty(property, value);
      });
    },

    /**
     * Initialize theme on app mount
     */
    initTheme() {
      const authStore = useAuthStore();
      this.setThemeFromRole(authStore.role);
    }
  }
});
